import { useCallback, useState } from 'react';
import type { O } from 'ts-toolbelt';

import MuiAccordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';

import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';
import type { Chip as CompleteChip, GenericId } from '@e-socna/data-layer';

import { useReferencesContext } from '../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { iconToMuiIcon } from '../../utils/icon-to-mui-icon.util';
import { colorToMuiColor } from '../../utils/color-to-mui-color.util';
import { formatAmount } from '../../utils/format-amount.util';
import { formatNumber } from '../../utils/format-number.util';
import { ThirdPartyName } from '../../components/ThirdPartyName';
import { RightDrawer } from '../../components/RightDrawer';
import { JSONPretty } from '../../components/JSONPretty';
import { formatPercent } from '../../utils/format-percent.util';
import { N_A } from '../../constants/labels.constant';

import { GenerateTable } from './GenerateTable';
import type { AccordionType } from './types/accordion.type';
import type { Row } from './types/row.type';
import type { Val } from './types/val.type';

type IChip = O.Pick<CompleteChip, 'color' | 'icon'>;

export const Accordion = () => {
  const {
    accountingStates,
    accountingWorkflows,
    actorCategories,
    actorTypes,
    addressableConfigs,
    addressTypes,
    archiveBinders,
    businessEventFileCategories,
    businessEventStates,
    businessEventTypes,
    businessEventVenues,
    businessEventWorkflows,
    civilities,
    codingStrategies,
    constructionTypes,
    contentOrigins,
    contexts,
    costFileCategories,
    costMargins,
    costTypes,
    currencies,
    currenciesIdMap,
    destinations,
    distanceRadius,
    documentTemplateFileCategories,
    documentTypes,
    edmDomains,
    entityRelationshipStrategies,
    fileDirectoryCategories,
    fileDirectoryTypes,
    fileOrigins,
    folderFileCategories,
    folderStates,
    folderWorkflows,
    genericStatuses,
    layerSources,
    materialFileCategories,
    missionFileCategories,
    missionStates,
    missionWorkflows,
    numberConstraints,
    organizationFunctions,
    paletteModes,
    pathologies,
    paymentMethods,
    paymentTerms,
    pennylaneUserAccounts,
    postalAddressStandards,
    externalReferenceTypes,
    printStyles,
    projections,
    quotationFileCategories,
    quotationStates,
    quotationWorkflows,
    references,
    reportFileCategories,
    reportStates,
    reportWorkflows,
    representativeTypes,
    resolvableMissionTypeFileCategories,
    resolvableServiceFileCategories,
    resolverGroups,
    resolverReturnTypes,
    resolvers,
    sectionTemplateFileCategories,
    sectionTypes,
    storageStrategies,
    thirdPartyCategories,
    thirdPartyFileCategories,
    thirdPartyTypes,
    timeUnits,
    toolkits,
    units,
    userActions,
    userFileCategories,
    userRoles,
    validityPeriods,
    vats,
  } = useReferencesContext();

  const [jsonDrawerContent, setJsonDrawerContent] =
    useState<object>(EMPTY_OBJECT);
  const [isJsonDrawerOpen, setIsJsonDrawerOpen] = useState(false);
  const handleDrawerClose = useCallback(() => {
    setIsJsonDrawerOpen(false);
  }, []);
  const openDrawer = useCallback((content: object) => {
    setJsonDrawerContent(content);
    setIsJsonDrawerOpen(true);
  }, []);

  const transformBoolean = (val: Val, _row: Row) => (val ? '✔' : EMPTY_STRING);

  const transformChip = (val: Val, row: Row) => (
    <Chip
      color={colorToMuiColor((val as IChip).color)}
      icon={iconToMuiIcon((val as IChip).icon)}
      // eslint-disable-next-line dot-notation
      label={(row['label'] as string | undefined) ?? EMPTY_STRING}
    />
  );

  const transformOrganization = (val: Val, _row: Row) =>
    val ? <ThirdPartyName id={val as GenericId} /> : 'Aucune';

  const transformShowIntegralContentButton = (_: unknown, row: Row) => (
    <Button
      // eslint-disable-next-line react/jsx-no-bind
      onClick={() => {
        openDrawer(row);
      }}
    >
      Afficher
    </Button>
  );

  const CODE_FIELD = { key: 'code', align: 'left', label: 'Code' } as const;
  const LABEL_FIELD = { key: 'label', align: 'left', label: 'Label' } as const;
  const DESCRIPTION_FIELD = {
    key: 'description',
    align: 'left',
    label: 'Description',
  } as const;
  const IS_SELECTED_BY_DEFAULT_FIELD = {
    key: 'isSelectedByDefault',
    align: 'center',
    label: 'Sélectionné par défaut ?',
    transform: transformBoolean,
  } as const;

  const FILE_CATEGORY_FIELDS = [
    CODE_FIELD,
    LABEL_FIELD,
    DESCRIPTION_FIELD,
    {
      key: 'pathSegment',
      align: 'left',
      label: 'Segment de chemin',
    },
    {
      key: 'hashFileName',
      align: 'center',
      label: 'Nom de fichier hashé ?',
      transform: transformBoolean,
    },
    {
      key: 'hideInFileExplorer',
      align: 'center',
      label: "Caché dans l'explorateur de fichiers ?",
      transform: transformBoolean,
    },
    {
      key: 'addTimestampToFileName',
      align: 'center',
      label: "Ajout de l'horodatage au nom de fichier ?",
      transform: transformBoolean,
    },
    {
      key: 'addRandomStringToFileName',
      align: 'center',
      label: "Ajout d'une chaîne aléatoire au nom de fichier ?",
      transform: transformBoolean,
    },
    {
      key: 'showIntegralContentButton',
      align: 'center',
      label: 'Contenu Intégral',
      transform: transformShowIntegralContentButton,
    },
    IS_SELECTED_BY_DEFAULT_FIELD,
  ] as const;

  const accordionList: readonly AccordionType[] = [
    {
      id: 'references',
      title: 'Liste des données du référentiel',
      data: references,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'activeVersion',
          align: 'right',
          label: 'Version active',
        },
      ],
    },
    {
      id: 'userActions',
      title: 'Actions utilisateurs',
      data: userActions,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'toolkits',
      title: 'Boîtes à outils',
      data: toolkits,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        { key: 'uriSegment', align: 'left', label: "Segment d'URI" },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'actorCategories',
      title: "Catégories d'acteurs",
      data: actorCategories,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'isInternal',
          align: 'center',
          label: 'Activé pour Acteur Interne ?',
          transform: transformBoolean,
        },
        {
          key: 'isExternal',
          align: 'center',
          label: 'Activé pour Acteur Externe ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'costFileCategories',
      title: 'Catégories de fichiers liés aux coûts',
      data: costFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'quotationFileCategories',
      title: 'Catégories de fichiers liés aux devis',
      data: quotationFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'folderFileCategories',
      title: 'Catégories de fichiers liés aux dossiers',
      data: folderFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'businessEventFileCategories',
      title: 'Catégories de fichiers liés aux événements commerciaux',
      data: businessEventFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'materialFileCategories',
      title: 'Catégories de fichiers liés aux matériaux',
      data: materialFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'missionFileCategories',
      title: 'Catégories de fichiers liés aux missions',
      data: missionFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'documentTemplateFileCategories',
      title: 'Catégories de fichiers liés aux modèles de documents',
      data: documentTemplateFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'sectionTemplateFileCategories',
      title: 'Catégories de fichiers liés aux modèles de sections',
      data: sectionTemplateFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'reportFileCategories',
      title: 'Catégories de fichiers liés aux rapports',
      data: reportFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'resolvableServiceFileCategories',
      title: 'Catégories de fichiers liés aux services résolvables',
      data: resolvableServiceFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'thirdPartyFileCategories',
      title: 'Catégories de fichiers liés aux tiers',
      data: thirdPartyFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'resolvableMissionTypeFileCategories',
      title: 'Catégories de fichiers liés aux types de mission',
      data: resolvableMissionTypeFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'userFileCategories',
      title: 'Catégories de fichiers liés aux utilisateurs',
      data: userFileCategories,
      fields: FILE_CATEGORY_FIELDS,
    },
    {
      id: 'fileDirectoryCategories',
      title: 'Catégories de répertoires de fichiers',
      data: fileDirectoryCategories,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'localPathSegment',
          align: 'left',
          label: 'Segment de chemin local',
        },
        {
          key: 'oneDrivePathSegment',
          align: 'left',
          label: 'Segment de chemin OneDrive',
        },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'thirdPartyCategories',
      title: 'Catégories de tiers',
      data: thirdPartyCategories,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'isInternal',
          align: 'center',
          label: 'Activé pour Tiers Interne ?',
          transform: transformBoolean,
        },
        {
          key: 'isExternal',
          align: 'center',
          label: 'Activé pour Tiers Externe ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'civilities',
      title: 'Civilités',
      data: civilities,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'abbreviation',
          align: 'left',
          label: 'Abréviation',
        },
        {
          key: 'isPlural',
          align: 'center',
          label: 'Forme plurielle ?',
          transform: transformBoolean,
        },
        {
          key: 'useWhenUnknown',
          align: 'center',
          label: 'Utiliser lorsque inconnu ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'archiveBinders',
      title: "Classeurs d'archives",
      data: archiveBinders,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'pennylaneUserAccounts',
      title: 'Comptes Utilisateur Pennylane',
      data: pennylaneUserAccounts,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'isEnabled',
          align: 'center',
          label: 'Activé ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'paymentTerms',
      title: 'Conditions de réglement',
      data: paymentTerms,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'addressableConfigs',
      title: "Configurations d'adresse",
      data: addressableConfigs,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'contexts',
      title: 'Contextes',
      data: contexts,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'numberConstraints',
      title: 'Contraintes sur les nombres',
      data: numberConstraints,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'destinations',
      title: 'Destinations',
      data: destinations,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'currencies',
      title: 'Devises',
      data: currencies,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        { key: 'isoCode', align: 'left', label: 'Code ISO' },
        { key: 'symbol', align: 'left', label: 'Symbole' },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'distanceRadius',
      title: 'Distances',
      data: distanceRadius,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'distanceInMeters',
          align: 'right',
          label: 'Distance en mètres',
          transform: (val) => formatNumber(val as number),
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'edmDomains',
      title: 'Domaines GED',
      data: edmDomains,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'validityPeriods',
      title: 'Durées de validité des devis',
      data: validityPeriods,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'durationInDays',
          align: 'right',
          label: 'Durée exprimée en jours',
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'accountingStates',
      title: 'États comptables',
      data: accountingStates,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'quotationStates',
      title: 'États des devis',
      data: quotationStates,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'folderStates',
      title: 'États des dossiers',
      data: folderStates,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'businessEventStates',
      title: 'États des événements métier',
      data: businessEventStates,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'missionStates',
      title: 'États des missions',
      data: missionStates,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'reportStates',
      title: 'États des rapports',
      data: reportStates,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'organizationFunctions',
      title: "Fonctions des membres de l'organisation",
      data: organizationFunctions,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'postalAddressStandards',
      title: "Formats d'adresse postale",
      data: postalAddressStandards,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'externalReferenceTypes',
      title: 'Types de référence externe',
      data: externalReferenceTypes,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'documentLabel',
          align: 'left',
          label: 'Libellé dans les documents',
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'resolverGroups',
      title: 'Groupes de résolveurs',
      data: resolverGroups,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'businessEventVenues',
      title: "Lieux d'événement métier",
      data: businessEventVenues,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'costMargins',
      title: 'Marges sur les coûts',
      data: costMargins.map((costMargin) => ({
        ...costMargin,
        costType:
          costTypes.find((costType) => costMargin.costTypeId === costType._id)
            ?.code ?? 'Inconnu',
        value: costMargin.fixedValue
          ? formatAmount(costMargin.fixedValue, currenciesIdMap)
          : costMargin.variableValue
            ? formatPercent(costMargin.variableValue)
            : N_A,
      })),
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'costType',
          align: 'left',
          label: 'Type de coût',
        },
        { key: 'value', align: 'right', label: 'Valeur' },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'paletteModes',
      title: "Modes d'affichage",
      data: paletteModes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'paymentMethods',
      title: 'Moyens de paiement',
      data: paymentMethods,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'contentOrigins',
      title: 'Origines des contenus',
      data: contentOrigins,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'fileOrigins',
      title: 'Origines des fichiers',
      data: fileOrigins,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'pathologies',
      title: 'Pathologies',
      data: pathologies,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'projections',
      title: 'Projections',
      data: projections,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'resolvers',
      title: 'Résolveurs',
      data: resolvers.map((documentResolver) => ({
        ...documentResolver,
        group:
          resolverGroups.find(
            (documentResolverGroup) =>
              documentResolverGroup._id === documentResolver.groupId,
          )?.label ?? 'Inconnu',
        returnType:
          resolverReturnTypes.find(
            (documentResolverReturnType) =>
              documentResolverReturnType._id === documentResolver.returnTypeId,
          )?.label ?? 'Inconnu',
      })),
      fields: [
        CODE_FIELD,
        { key: 'command', align: 'left', label: 'Commande' },
        DESCRIPTION_FIELD,
        { key: 'group', align: 'left', label: 'Groupe' },
        { key: 'returnType', align: 'left', label: 'Type de retour' },
        {
          key: 'isResolvableOnFront',
          align: 'center',
          label: 'Résolvable sur le front ?',
          transform: transformBoolean,
        },
        {
          key: 'isResolvableOnBack',
          align: 'center',
          label: 'Résolvable sur le serveur ?',
          transform: transformBoolean,
        },
        {
          key: 'enableDirectGeneration',
          align: 'center',
          label: 'Générable directement ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'userRoles',
      title: 'Rôles utilisateurs e-SOCNA',
      data: userRoles,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'isGlobal',
          align: 'center',
          label: 'Global ?',
          transform: transformBoolean,
        },
        {
          key: 'isContextual',
          align: 'center',
          label: 'Contextuel ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'layerSources',
      title: 'Sources de calques',
      data: layerSources,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'genericStatuses',
      title: 'Statuts Génériques',
      data: genericStatuses,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'storageStrategies',
      title: 'Stratégie de stockage des fichiers',
      data: storageStrategies,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        { key: 'readPriority', align: 'right', label: 'Priorité de lecture' },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'codingStrategies',
      title: 'Stratégies de codification',
      data: codingStrategies,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'chip',
          align: 'left',
          label: 'Pastille',
          transform: transformChip,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'entityRelationshipStrategies',
      title: 'Stratégies de liaison des entités',
      data: entityRelationshipStrategies,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'printStyles',
      title: 'Styles des impressions',
      data: printStyles,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'relatedOrganizationId',
          align: 'left',
          label: "Société SOCNA liée au style d'impression",
          transform: transformOrganization,
        },
        { key: 'logoImageUri', align: 'left', label: 'URI du logo' },
        {
          key: 'footerLineOne',
          align: 'left',
          label: 'Ligne de pied de page 1',
        },
        {
          key: 'footerLineTwo',
          align: 'left',
          label: 'Ligne de pied de page 2',
        },
        {
          key: 'footerLineThree',
          align: 'left',
          label: 'Ligne de pied de page 3',
        },
        { key: 'primaryColor', align: 'left', label: 'Couleur primaire' },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'vats',
      title: 'TVA',
      data: vats,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        { key: 'value', align: 'right', label: 'Valeur' },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'actorTypes',
      title: "Types d'acteur",
      data: actorTypes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'addressTypes',
      title: "Types d'adresse",
      data: addressTypes,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'isPennylaneMainAddress',
          align: 'center',
          label: 'Adresse principale sur Pennylane ?',
          transform: transformBoolean,
        },
        {
          key: 'isPennylaneDeliveryAddress',
          align: 'center',
          label: 'Adresse de livraison sur Pennylane ?',
          transform: transformBoolean,
        },
        {
          key: 'requiresPostalInfo',
          align: 'center',
          label: 'Requiert des informations postales ?',
          transform: transformBoolean,
        },
        {
          key: 'requiresLocationInfo',
          align: 'center',
          label: 'Requiert des informations de localisation ?',
          transform: transformBoolean,
        },
        {
          key: 'requiresCadastralInfo',
          align: 'center',
          label: 'Requiert des informations cadastrales ?',
          transform: transformBoolean,
        },
        {
          key: 'usedByDistanceCalculator',
          align: 'center',
          label: 'Utilisé par le calculateur de distance ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'businessEventTypes',
      title: "Types d'événement métier",
      data: businessEventTypes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'constructionTypes',
      title: 'Types de construction',
      data: constructionTypes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'costTypes',
      title: 'Types de coûts',
      data: costTypes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'documentTypes',
      title: 'Types de documents',
      data: documentTypes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'fileDirectoryTypes',
      title: 'Types de répertoire de fichier',
      data: fileDirectoryTypes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'representativeTypes',
      title: 'Types de représentant',
      data: representativeTypes,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'isInternal',
          align: 'center',
          label: 'Activé pour Acteur Interne ?',
          transform: transformBoolean,
        },
        {
          key: 'isExternal',
          align: 'center',
          label: 'Activé pour Acteur Externe ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'resolverReturnTypes',
      title: 'Types de retour de résolveurs',
      data: resolverReturnTypes,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        { key: 'typeName', align: 'left', label: 'TypeName' },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'sectionTypes',
      title: 'Types de sections',
      data: sectionTypes,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        {
          key: 'needsPrintFeedbacks',
          align: 'center',
          label: "Nécessite le traitement des feedbacks d'impression ?",
          transform: transformBoolean,
        },
        {
          key: 'supportPageCount',
          align: 'center',
          label: 'Inclure dans le comptage des pages ?',
          transform: transformBoolean,
        },
        {
          key: 'hideHeaders',
          align: 'center',
          label: 'Cacher les entêtes de page ?',
          transform: transformBoolean,
        },
        {
          key: 'hideFooters',
          align: 'center',
          label: 'Cacher les pieds de page ?',
          transform: transformBoolean,
        },
        {
          key: 'oneChildMax',
          align: 'center',
          label: 'Seulement une section maximum ?',
          transform: transformBoolean,
        },
        {
          key: 'forcePagebreaks',
          align: 'center',
          label: 'Forcer les sauts de page ?',
          transform: transformBoolean,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'thirdPartyTypes',
      title: 'Types de tiers',
      data: thirdPartyTypes,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'units',
      title: 'Unités',
      data: units,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        { key: 'unit', align: 'left', label: 'Unité' },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'timeUnits',
      title: 'Unités de temps',
      data: timeUnits,
      fields: [CODE_FIELD, LABEL_FIELD, IS_SELECTED_BY_DEFAULT_FIELD],
    },
    {
      id: 'accountingWorkflows',
      title: 'Workflows comptables',
      data: accountingWorkflows,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'isEnabled',
          align: 'center',
          label: 'Activé ?',
          transform: transformBoolean,
        },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'quotationWorkflows',
      title: 'Workflows des devis',
      data: quotationWorkflows,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'isEnabled',
          align: 'center',
          label: 'Activé ?',
          transform: transformBoolean,
        },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'folderWorkflows',
      title: 'Workflows des dossiers',
      data: folderWorkflows,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'isEnabled',
          align: 'center',
          label: 'Activé ?',
          transform: transformBoolean,
        },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'businessEventWorkflows',
      title: 'Workflows des événements métier',
      data: businessEventWorkflows,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'isEnabled',
          align: 'center',
          label: 'Activé ?',
          transform: transformBoolean,
        },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'missionWorkflows',
      title: 'Workflows des missions',
      data: missionWorkflows,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'isEnabled',
          align: 'center',
          label: 'Activé ?',
          transform: transformBoolean,
        },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
    {
      id: 'reportWorkflows',
      title: 'Workflows des rapports',
      data: reportWorkflows,
      fields: [
        CODE_FIELD,
        LABEL_FIELD,
        DESCRIPTION_FIELD,
        {
          key: 'isEnabled',
          align: 'center',
          label: 'Activé ?',
          transform: transformBoolean,
        },
        {
          key: 'showIntegralContentButton',
          align: 'center',
          label: 'Contenu Intégral',
          transform: transformShowIntegralContentButton,
        },
        IS_SELECTED_BY_DEFAULT_FIELD,
      ],
    },
  ];

  const generateAccordion = (accordion: Readonly<AccordionType>) => (
    <MuiAccordion
      key={accordion.id}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary
        aria-controls={`panel-${accordion.id}-content`}
        expandIcon={<ExpandMoreIcon />}
        id={`panel-${accordion.id}-header`}
      >
        <Typography>{accordion.title}</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <GenerateTable accordion={accordion} />
      </AccordionDetails>
    </MuiAccordion>
  );

  return (
    <>
      {accordionList.map(generateAccordion)}{' '}
      <RightDrawer
        isOpen={isJsonDrawerOpen}
        onDrawerClose={handleDrawerClose}
        title="Contenu Intégral"
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <JSONPretty
            data={jsonDrawerContent}
            id="reference-integral-content"
          />
        </Box>
      </RightDrawer>
    </>
  );
};
